import { useGetIpAddressRestrictionSetting } from '@/context/services/ip_address_restriction/ip_address_restriction_setting.service';
import {
  useCreateIpAddressWhitelistService,
  useDeleteIpAddressWhitelistService,
  useGetIpAddressWhitelistsService,
  useUpdateIpAddressWhitelistService,
} from '@/context/services/ip_address_whitelist/ip_address_whitelist.service';
import {
  type IpAddressWhitelistUpsertFormValues,
  type IpAddressWhitelistUpsertModalProps,
} from '@/features/ip_address_whitelists/components/IpAddressWhitelistUpsertModal/IpAddressWhitelistUpsertModal';
import { useIpAddressWhitelistDeleteConfirmModal } from '@/features/ip_address_whitelists/hooks/useIpAddressWhitelistDeleteConfirmModal';
import { useIpAddressWhitelistMutationSuccessToast } from '@/features/ip_address_whitelists/hooks/useIpAddressWhitelistMutationSuccessToast';
import type { IpAddressWhitelistInlineFormValues } from '@/features/ip_address_whitelists/IpAddressWhitelistList/types';
import type { IpAddressWhitelistTableRow } from '@/features/ip_address_whitelists/type';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { type FC, useCallback, useEffect, useState } from 'react';
import { type Control, useForm } from 'react-hook-form';

type Args = {
  onIsEditingChange?: (isEditing: boolean) => void;
  onIsEmptyChange?: (isEmpty: boolean) => void;
  setInlineError?: (error: Error) => void;
  officeId: string;
};

export type UseIpAddressWhitelistListResult = {
  whitelistRows: IpAddressWhitelistTableRow[];
  isWhitelistEmpty: boolean;
  isDeleteBlockedByRestriction: boolean;
  isMutating: boolean;
  isAddModalOpen: boolean;
  editingRowId: string | null;
  inlineFormControl: Control<IpAddressWhitelistInlineFormValues>;
  isInlineSaveDisabled: boolean;
  onSaveInline: () => void;
  onCancelInline: () => void;
  onClickAdd: () => void;
  onStartEditRow: (row: IpAddressWhitelistTableRow) => void;
  onDeleteRow: (row: IpAddressWhitelistTableRow) => void | Promise<void>;
  onCloseAddModal: () => void;
  onAddSubmit: IpAddressWhitelistUpsertModalProps['onSubmit'];
  ConfirmDeleteModal: FC;
};

export const useIpAddressWhitelistList = ({
  onIsEditingChange,
  onIsEmptyChange,
  setInlineError,
  officeId,
}: Args): UseIpAddressWhitelistListResult => {
  const { data } = useGetIpAddressWhitelistsService();
  const whitelistRows: IpAddressWhitelistTableRow[] =
    data?.ip_address_whitelists ?? [];
  const isWhitelistEmpty = whitelistRows.length === 0;

  const { data: restrictionData } = useGetIpAddressRestrictionSetting();
  const isDeleteBlockedByRestriction =
    (restrictionData?.ip_address_restriction_enabled ?? false) &&
    whitelistRows.length === 1;

  useEffect(() => {
    onIsEmptyChange?.(isWhitelistEmpty);
  }, [isWhitelistEmpty, onIsEmptyChange]);

  const { mutateAsync: createAsync } =
    useCreateIpAddressWhitelistService(officeId);
  const { mutateAsync: updateAsync } =
    useUpdateIpAddressWhitelistService(officeId);
  const { mutateAsync: deleteAsync } =
    useDeleteIpAddressWhitelistService(officeId);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);

  const inlineForm = useForm<IpAddressWhitelistInlineFormValues>({
    mode: 'onChange',
    defaultValues: { ip_address: '' },
  });
  const {
    control: inlineFormControl,
    reset: resetInlineForm,
    handleSubmit: handleInlineSubmit,
    formState: inlineFormState,
    setError: setInlineFieldError,
  } = inlineForm;

  const { confirm: confirmDelete, ConfirmModal: ConfirmDeleteModal } =
    useIpAddressWhitelistDeleteConfirmModal();
  const { notifyMutationSuccess } = useIpAddressWhitelistMutationSuccessToast();
  const { t } = useTranslation();
  const notify = useToastAPI();

  const resolveErrorMessage = useCallback(
    (messages: string[] | undefined): string =>
      messages && messages.length > 0 ? messages.join('\n') : t('system_error'),
    [t]
  );

  const openAddModal = useCallback(() => {
    setIsAddModalOpen(true);
    onIsEditingChange?.(true);
  }, [onIsEditingChange]);

  const closeAddModal = useCallback(() => {
    setIsAddModalOpen(false);
    if (inlineEditId === null) onIsEditingChange?.(false);
  }, [inlineEditId, onIsEditingChange]);

  const startInlineEdit = useCallback(
    (row: IpAddressWhitelistTableRow) => {
      setInlineEditId(row.id);
      resetInlineForm({ ip_address: row.ip_address });
      onIsEditingChange?.(true);
    },
    [onIsEditingChange, resetInlineForm]
  );

  const cancelInlineEdit = useCallback(() => {
    setInlineEditId(null);
    resetInlineForm({ ip_address: '' });
    if (!isAddModalOpen) onIsEditingChange?.(false);
  }, [isAddModalOpen, onIsEditingChange, resetInlineForm]);

  const saveInlineEdit = useCallback(() => {
    if (!inlineEditId) return;
    handleInlineSubmit(async (values) => {
      try {
        const result = await updateAsync(
          inlineEditId,
          values.ip_address.trim()
        );
        if (result.status === 'success') {
          notifyMutationSuccess('update');
          cancelInlineEdit();
          return;
        }
        if (result.status === 'forbidden') {
          if (result.redirect_url) {
            location.href = result.redirect_url;
          } else {
            cancelInlineEdit();
            setInlineError?.(
              new Error(resolveErrorMessage(result.errors?.messages))
            );
            return;
          }
        } else {
          const messages = result.errors?.messages ?? [];
          if (messages.length > 0) {
            setInlineFieldError('ip_address', {
              type: 'server',
              message: messages.join('\n'),
            });
            return;
          }
        }
        notify.error({ title: t('system_error'), duration: 5 });
      } catch {
        notify.error({ title: t('system_error'), duration: 5 });
      }
    })();
  }, [
    cancelInlineEdit,
    handleInlineSubmit,
    inlineEditId,
    notify,
    notifyMutationSuccess,
    resolveErrorMessage,
    setInlineError,
    setInlineFieldError,
    t,
    updateAsync,
  ]);

  const handleAddSubmit = useCallback<
    IpAddressWhitelistUpsertModalProps['onSubmit']
  >(
    async (values: IpAddressWhitelistUpsertFormValues) => {
      const result = await createAsync(values.ip_address.trim());
      if (result.status === 'success') {
        notifyMutationSuccess('add');
        closeAddModal();
        return {};
      }
      if (result.status === 'forbidden') {
        if (result.redirect_url) {
          location.href = result.redirect_url;
        } else {
          closeAddModal();
          setInlineError?.(
            new Error(resolveErrorMessage(result.errors?.messages))
          );
          return;
        }
      }
      return {
        errorMessage: resolveErrorMessage(result.errors?.messages),
      };
    },
    [
      closeAddModal,
      createAsync,
      notifyMutationSuccess,
      resolveErrorMessage,
      setInlineError,
    ]
  );

  const onDeleteRow = useCallback(
    async (row: IpAddressWhitelistTableRow) => {
      const confirmed = await confirmDelete();
      if (!confirmed) return;
      setIsMutating(true);
      try {
        const result = await deleteAsync(row.id);
        if (result.status === 'success') {
          notifyMutationSuccess('delete');
          return;
        }
        const errorMessage = resolveErrorMessage(result.errors?.messages);
        if (result.status === 'forbidden') {
          if (result.redirect_url) {
            location.href = result.redirect_url;
          } else {
            setInlineError?.(new Error(errorMessage));
            return;
          }
        }
        notify.error({ title: errorMessage, duration: 5 });
      } finally {
        setIsMutating(false);
      }
    },
    [
      confirmDelete,
      deleteAsync,
      notify,
      notifyMutationSuccess,
      resolveErrorMessage,
      setInlineError,
    ]
  );

  const isInlineSaveDisabled =
    inlineEditId !== null &&
    (!inlineFormState.isValid || inlineFormState.isSubmitting);

  return {
    whitelistRows,
    isWhitelistEmpty,
    isDeleteBlockedByRestriction,
    isMutating,
    isAddModalOpen,
    editingRowId: inlineEditId,
    inlineFormControl,
    isInlineSaveDisabled,
    onSaveInline: saveInlineEdit,
    onCancelInline: cancelInlineEdit,
    onClickAdd: openAddModal,
    onStartEditRow: startInlineEdit,
    onDeleteRow,
    onCloseAddModal: closeAddModal,
    onAddSubmit: handleAddSubmit,
    ConfirmDeleteModal,
  };
};
